import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  Copy,
  ExternalLink,
  RefreshCw,
  Search,
  ShieldAlert,
  Users,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Badge, Card } from '@/components/ui'
import { cn } from '@/lib/utils'
import {
  detectionLagDays,
  formatIncidentAmounts,
  groupSourceIncidents,
  hasBlankStudioSlice,
  incidentTitle,
  stableIncidentToSourceIncident,
  type SourceIncident,
} from '@/lib/revenueAudit'
import {
  fetchMissingPaymentsControlItems,
  fetchMissingPaymentsControlItemsPage,
  fetchMissingPaymentsControlRuns,
  fetchMissingPaymentsControlSummary,
  fetchRevenueAccountDirectory,
  type MissingPaymentsControlItem,
  type MissingPaymentsControlRun,
  type MissingPaymentsControlSummaryRow,
  type MissingPaymentsIssueType,
  type RevenueAccountDirectoryRow,
} from '@/services/missingPaymentsControlService'
import { fetchCrmSourceAccounts, type CrmSourceAccountRow } from '@/services/crmSourceAccountService'
import {
  fetchRevenueSyncIncidents,
  type RevenueSyncIncidentRow,
} from '@/services/revenueSyncIncidentService'

/**
 * Экран — read-only аудит синхронизации платежей
 * (`docs/MISSING_PAYMENTS_SCREEN_FRONTEND_DESIGN.md`). Управление источником
 * (карантин, постоянное закрытие, возврат в работу) живёт только на
 * `/management/crm-accounts`: здесь платёж — доказательство инцидента, а не
 * задача оператору, поэтому действий, меняющих данные, тут нет.
 */

type AuditTab = 'open' | 'history' | 'unattributed' | 'runs'

const TABS: Array<{ value: AuditTab; label: string; hint: string }> = [
  {
    value: 'open',
    label: 'Открытые',
    hint: 'Платежи, которые ListokCRM перестал присылать, а защита оставила живыми',
  },
  {
    value: 'history',
    label: 'История изменений',
    hint: 'Платежи, уже исключённые из выручки: свершившийся факт, а не задача',
  },
  {
    value: 'unattributed',
    label: 'Без атрибуции',
    hint: 'Активные платежи, у которых не определён филиал',
  },
  { value: 'runs', label: 'Прогоны', hint: 'Последние прогоны Revenue ETL и вердикт preflight' },
]

const TAB_ISSUE_TYPES: Record<Exclude<AuditTab, 'runs'>, MissingPaymentsIssueType[]> = {
  open: ['missing'],
  history: ['deleted', 'reissue'],
  unattributed: ['unattributed'],
}

/**
 * Глубина, на которой ETL вообще способен заново обнаружить пропажу платежа,
 * равна окну ночного синка: платёж старше него из выгрузки не запрашивается.
 * Поэтому окно дат платежа по умолчанию — 35 дней, а не 7.
 *
 * ⚠️ Интерим: живой RPC одним окном фильтрует И дату обнаружения, И дату
 * платежа, поэтому фильтр «Обнаружено» применяется клиентски. Волна B плана —
 * раздельные `p_detected_*` / `p_paid_*` на сервере.
 */
const SYNC_WINDOW_DAYS = 35
const ITEMS_PAGE_SIZE = 200

const DETECTED_WITHIN_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 7, label: 'Обнаружено за 7 дней' },
  { value: 30, label: 'Обнаружено за 30 дней' },
  { value: 0, label: 'Обнаружено — все' },
]

const ACCOUNT_CLASS_LABELS: Record<string, string> = {
  franchisee: 'Не выбран филиал',
  no_numbered_studios: 'Служебный аккаунт',
  unknown: 'Аккаунт не найден',
}

const ACCOUNT_CLASS_HINTS: Record<string, string> = {
  franchisee:
    'На этом CRM-аккаунте есть студии, но у платежа не выбран филиал. Исправьте филиал платежа в ListokCRM.',
  no_numbered_studios:
    'На этом CRM-аккаунте нет номерных студий. Обычно это служебный или тестовый аккаунт.',
  unknown: 'Аккаунт не найден в справочнике платежей.',
}

const ACCOUNT_CLASS_TONES: Record<string, string> = {
  franchisee: 'border-red-200 bg-red-50 text-red-700',
  no_numbered_studios: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  unknown: 'border-zinc-200 bg-zinc-50 text-zinc-600',
}

const SOURCE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'В работе',
  QUARANTINED: 'Карантин',
  RETIRED: 'Закрыт навсегда',
}

const SOURCE_STATUS_TONES: Record<string, string> = {
  ACTIVE: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  QUARANTINED: 'border-sky-200 bg-sky-50 text-sky-700',
  RETIRED: 'border-zinc-300 bg-zinc-100 text-zinc-700',
}

function isoDate(daysAgo: number): string {
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date.toISOString().slice(0, 10)
}

function nextDate(value: string): string {
  const date = new Date(`${value}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('ru-RU')
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatAmount(value: number | null | undefined, currency?: string | null): string {
  const amount = Number(value ?? 0)
  const code = currency || 'RUB'
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${code}`
  }
}

function buildListokAccountUrl(account: string): string | null {
  const template =
    (import.meta.env.VITE_LISTOK_ACCOUNT_URL_TEMPLATE as string | undefined) ??
    'https://{account}.listokcrm.ru/payment/income'
  return template.replace('{account}', encodeURIComponent(account))
}

function ownerLabel(incident: SourceIncident): string {
  if (incident.personsFio) {
    return `${incident.personsFio}${incident.partnerNumber ? ` · №${incident.partnerNumber}` : ''}`
  }
  if (incident.accountFio) {
    return `${incident.accountFio} · по CRM-аккаунту`
  }
  return 'Франчайзи не определён'
}

function matchesSearch(incident: SourceIncident, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    incident.account,
    incident.accountCityName,
    incident.accountFio,
    incident.personsFio,
    incident.partnerNumber,
    ...incident.studios.map(studio => studio.studioNumber),
    ...incident.studios.map(studio => studio.cityName),
    ...incident.studios.map(studio => studio.address),
  ]
    .filter(value => value !== null && value !== undefined)
    .join(' ')
    .toLowerCase()
    .includes(q)
}

function daysSince(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = Date.parse(`${value.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed)) return null
  return Math.max(0, Math.floor((Date.now() - parsed) / 86_400_000))
}

function KpiCard({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <Card className="p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-xl font-semibold', tone)}>{value}</div>
    </Card>
  )
}

function AccountLink({ account }: { account: string }) {
  const url = buildListokAccountUrl(account)
  if (!url) return <span>{account}</span>

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      {account}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

function SourceStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-[11px] font-medium',
        SOURCE_STATUS_TONES[status] ?? SOURCE_STATUS_TONES.ACTIVE
      )}
    >
      {SOURCE_STATUS_LABELS[status] ?? status}
    </span>
  )
}

function AccountClassBadge({ accountClass }: { accountClass: string }) {
  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-[11px] font-medium',
        ACCOUNT_CLASS_TONES[accountClass] ?? ACCOUNT_CLASS_TONES.unknown
      )}
      title={ACCOUNT_CLASS_HINTS[accountClass] ?? ACCOUNT_CLASS_HINTS.unknown}
    >
      {ACCOUNT_CLASS_LABELS[accountClass] ?? ACCOUNT_CLASS_LABELS.unknown}
    </span>
  )
}

function IncidentRow({
  incident,
  source,
  selected,
  onSelect,
}: {
  incident: SourceIncident
  source: CrmSourceAccountRow | null
  selected: boolean
  onSelect: () => void
}) {
  const blankSlice = hasBlankStudioSlice(incident)

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'grid w-full grid-cols-1 gap-2 border-t border-border px-3 py-3 text-left text-sm transition-colors md:grid-cols-[1.6fr_0.9fr_0.9fr]',
        selected ? 'bg-primary/5' : 'hover:bg-muted/40'
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{incidentTitle(incident)}</span>
          {source && source.source_status !== 'ACTIVE' && (
            <SourceStatusBadge status={source.source_status} />
          )}
          {incident.issueType === 'unattributed' && incident.accountClass && (
            <AccountClassBadge accountClass={incident.accountClass} />
          )}
          {blankSlice && incident.studios.length > 1 && (
            <span
              className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] text-zinc-600"
              title="Внутри инцидента есть платежи без номера филиала — это его деталь, а не отдельная проблема"
            >
              + без номера
            </span>
          )}
        </div>
        <div className="mt-1 truncate text-xs text-muted-foreground">{ownerLabel(incident)}</div>
        {incident.stableIncident && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            incident #{incident.stableIncident.incident_id} · открыт {formatDateTime(incident.stableIncident.opened_at)}
          </div>
        )}
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Платежи</div>
        <div className="font-medium">{incident.paymentCount}</div>
        <div className="text-xs text-muted-foreground">
          {formatIncidentAmounts(incident.totals, formatAmount)}
        </div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground">Платежи от</div>
        <div className="font-medium">
          {formatDate(incident.oldestPaidOn)} - {formatDate(incident.newestPaidOn)}
        </div>
        <div className="text-xs text-muted-foreground">
          обнаружено {formatDate(incident.lastDetectedAt)}
        </div>
      </div>
    </button>
  )
}

function IncidentDetails({
  incident,
  source,
  items,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpenAccount,
}: {
  incident: SourceIncident | null
  source: CrmSourceAccountRow | null
  items: MissingPaymentsControlItem[]
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  onOpenAccount: (account: string) => void
}) {
  if (!incident) {
    return (
      <Card className="p-4">
        <div className="text-sm text-muted-foreground">
          Выберите инцидент, чтобы увидеть платежи-доказательства.
        </div>
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-border px-3 py-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold">{incidentTitle(incident)}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              <AccountLink account={incident.account} /> ·{' '}
              {formatIncidentAmounts(incident.totals, formatAmount)} · {incident.paymentCount} платежей
            </div>
            {incident.stableIncident && (
              <div className="mt-1 text-xs text-muted-foreground">
                OPEN · наблюдался {formatDateTime(incident.stableIncident.last_seen_at)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => onOpenAccount(incident.account)}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium hover:bg-muted"
          >
            <Users className="h-3.5 w-3.5" />
            Открыть CRM-аккаунт
          </button>
        </div>

        {incident.studios.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {incident.studios.map(studio => (
              <span
                key={studio.studioNumber ?? 'none'}
                className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px]"
              >
                {studio.studioNumber == null ? 'без номера' : `№${studio.studioNumber}`} ·{' '}
                {studio.paymentCount} · {formatIncidentAmounts(studio.totals, formatAmount)}
              </span>
            ))}
          </div>
        )}

        {source?.source_status === 'RETIRED' && (
          <div className="mt-2 rounded border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-700">
            Источник закрыт навсегда{source.exception_reason ? `: ${source.exception_reason}` : ''}.
            Данные оставлены живыми, возврата выгрузки не ждём.
          </div>
        )}
        {source?.source_status === 'QUARANTINED' && (
          <div className="mt-2 rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs text-sky-800">
            Источник в карантине{source.exception_reason ? `: ${source.exception_reason}` : ''}.
            Снять карантин можно только на экране CRM-аккаунтов.
          </div>
        )}
        {incident.issueType === 'unattributed' && incident.accountClass === 'franchisee' && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
            У этих платежей не выбран филиал, поэтому они не попадают в выручку. Выберите филиал в
            ListokCRM; после следующей синхронизации строка исчезнет.
          </div>
        )}
      </div>

      {loading ? (
        <div className="px-3 py-8 text-center text-sm text-muted-foreground">Загружаем платежи...</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-8 text-center text-sm text-muted-foreground">Платежи не найдены</div>
      ) : (
        <div className="max-h-[560px] overflow-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="sticky top-0 bg-muted text-muted-foreground">
              <tr>
                <th className="px-2 py-2 font-medium">Платёж от</th>
                <th className="px-2 py-2 font-medium">Обнаружено</th>
                <th className="px-2 py-2 font-medium">Сумма</th>
                <th className="px-2 py-2 font-medium">Студия</th>
                <th className="px-2 py-2 font-medium">Клиент</th>
                <th className="px-2 py-2 font-medium">Платёж</th>
                <th className="px-2 py-2 font-medium">Основание</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const lag = detectionLagDays(item.paid_at, item.detected_at)
                return (
                  <tr
                    key={`${item.issue_type}:${item.account}:${item.payment_id}:${item.detected_at}`}
                    className="border-t border-border"
                  >
                    <td className="whitespace-nowrap px-2 py-2">{formatDate(item.paid_at)}</td>
                    <td className="whitespace-nowrap px-2 py-2">
                      <div>{formatDateTime(item.detected_at)}</div>
                      {lag != null && <div className="text-muted-foreground">лаг {lag} дн.</div>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 font-medium">
                      {formatAmount(item.amount, item.currency)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      {item.effective_studio_number != null
                        ? `№${item.effective_studio_number}`
                        : 'без номера'}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-medium">{item.client_name || '-'}</div>
                      <div className="text-muted-foreground">
                        {item.client_phone || item.client_id || ''}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(item.payment_id)}
                        className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-muted"
                        title="Скопировать payment_id"
                      >
                        {item.payment_id}
                        <Copy className="h-3 w-3" />
                      </button>
                      <div className="text-muted-foreground">{item.payment_method || '-'}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div>{item.category || '-'}</div>
                      <div className="text-muted-foreground">{item.basis || item.added_by || ''}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {hasMore && (
            <div className="border-t border-border p-2 text-center">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className="inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                {loadingMore ? 'Загружаем...' : 'Показать ещё'}
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function RunsPanel({ runs }: { runs: MissingPaymentsControlRun[] }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-border px-3 py-2 text-sm font-semibold">Последние прогоны ETL</div>
      {runs.length === 0 ? (
        <div className="px-3 py-6 text-sm text-muted-foreground">Прогонов нет</div>
      ) : (
        runs.map(run => {
          const missingCount = run.missing_accounts.length + run.missing_pairs.length
          return (
            <div key={run.logical_run_id} className="border-t border-border px-3 py-3 text-xs first:border-t-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium">
                  {run.mode || 'run'} · {formatDate(run.window_start)} - {formatDate(run.window_end)}
                </div>
                <Badge>{run.status}</Badge>
              </div>
              <div className="mt-1 text-muted-foreground">
                preflight: {run.preflight_status || '-'} · строк {run.row_count.toLocaleString('ru-RU')} · аккаунтов {run.account_count}
              </div>
              {(missingCount > 0 || run.quarantined.length > 0) && (
                <div className="mt-2 rounded border border-border bg-muted/30 px-2 py-1.5">
                  {missingCount > 0 && <div>Не пришли из CRM аккаунты/пары: {missingCount}</div>}
                  {run.quarantined.length > 0 && <div>Исключены карантином: {run.quarantined.length}</div>}
                </div>
              )}
              {run.error_message && <div className="mt-2 text-red-700">{run.error_message}</div>}
              <div className="mt-1 text-muted-foreground">Обновлён {formatDateTime(run.updated_at)}</div>
            </div>
          )
        })
      )}
    </Card>
  )
}

export function MissingPaymentsControlPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const deepLinkIncident = searchParams.get('incident')
  const [tab, setTab] = useState<AuditTab>('open')
  const [dateFrom, setDateFrom] = useState(isoDate(SYNC_WINDOW_DAYS))
  const [dateTo, setDateTo] = useState(isoDate(0))
  const [detectedWithinDays, setDetectedWithinDays] = useState(7)
  const [historyPaidDateFilter, setHistoryPaidDateFilter] = useState(false)
  // Приход с мастер-экрана источников: `?account=an7221` — это «покажи
  // платёжные доказательства по этому аккаунту», поэтому сразу в поиск.
  const [search, setSearch] = useState(() => searchParams.get('account') ?? '')
  const [summary, setSummary] = useState<MissingPaymentsControlSummaryRow[]>([])
  // null means the stable registry is unavailable, not that it is empty.
  // In that case keep the legacy journal-derived queue visible as a fallback.
  const [openSyncIncidents, setOpenSyncIncidents] = useState<RevenueSyncIncidentRow[] | null>(null)
  const [accounts, setAccounts] = useState<Map<string, RevenueAccountDirectoryRow>>(new Map())
  const [sourceAccounts, setSourceAccounts] = useState<Map<string, CrmSourceAccountRow>>(new Map())
  const [runs, setRuns] = useState<MissingPaymentsControlRun[]>([])
  const [items, setItems] = useState<MissingPaymentsControlItem[]>([])
  const [itemsHasMore, setItemsHasMore] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemsLoadingMore, setItemsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const journalIncidents = useMemo(() => groupSourceIncidents(summary, accounts), [accounts, summary])
  const stableOpenIncidents = useMemo(
    () =>
      (openSyncIncidents ?? [])
        .map(incident => stableIncidentToSourceIncident(incident, accounts))
        .filter((incident): incident is SourceIncident => incident !== null),
    [accounts, openSyncIncidents]
  )
  const operationalOpenIncidents = useMemo(
    () =>
      openSyncIncidents === null
        ? journalIncidents.filter(incident => incident.issueType === 'missing')
        : stableOpenIncidents,
    [journalIncidents, openSyncIncidents, stableOpenIncidents]
  )

  const tabIncidents = useMemo(() => {
    if (tab === 'runs') return []
    if (tab === 'open') return operationalOpenIncidents.filter(incident => matchesSearch(incident, search))
    const issueTypes = TAB_ISSUE_TYPES[tab]
    const cutoff =
      tab === 'history' && detectedWithinDays > 0
        ? Date.now() - detectedWithinDays * 86_400_000
        : null

    return journalIncidents.filter(incident => {
      if (!issueTypes.includes(incident.issueType)) return false
      if (!matchesSearch(incident, search)) return false
      // «Открытые» намеренно не ограничены датой обнаружения: наблюдение видно,
      // пока источник его не закрыл. Дата обнаружения фильтрует только историю.
      if (cutoff != null) {
        const detected = incident.lastDetectedAt ? Date.parse(incident.lastDetectedAt) : null
        if (detected == null || Number.isNaN(detected) || detected < cutoff) return false
      }
      return true
    })
  }, [detectedWithinDays, journalIncidents, operationalOpenIncidents, search, tab])

  const selected = useMemo(
    () => tabIncidents.find(incident => incident.key === selectedKey) ?? null,
    [selectedKey, tabIncidents]
  )

  const selectedSource = useMemo(
    () => (selected ? sourceAccounts.get(selected.account) ?? null : null),
    [selected, sourceAccounts]
  )

  const quarantinedIncidents = useMemo(
    () => journalIncidents.filter(incident => incident.issueType === 'quarantined'),
    [journalIncidents]
  )

  const stats = useMemo(() => {
    const openCount = operationalOpenIncidents.length
    const deleted = journalIncidents.filter(incident => incident.issueType === 'deleted')
    const deletedTotals = new Map<string, number>()
    for (const incident of deleted) {
      for (const total of incident.totals) {
        deletedTotals.set(total.currency, (deletedTotals.get(total.currency) ?? 0) + total.absAmount)
      }
    }
    const offBranch = journalIncidents.filter(
      incident => incident.issueType === 'unattributed' && incident.accountClass === 'franchisee'
    ).length
    const service = journalIncidents.filter(
      incident =>
        incident.issueType === 'unattributed' && incident.accountClass === 'no_numbered_studios'
    ).length

    return {
      openCount,
      quarantined: quarantinedIncidents.length,
      offBranch,
      service,
      deletedCount: deleted.length,
      deletedAmountText:
        Array.from(deletedTotals.entries())
          .map(([currency, amount]) => formatAmount(amount, currency))
          .join(' · ') || formatAmount(0, 'RUB'),
    }
  }, [journalIncidents, operationalOpenIncidents, quarantinedIncidents])

  const lastRolling35 = useMemo(
    () => runs.find(run => run.mode === 'rolling35' && run.status === 'applied') ?? null,
    [runs]
  )

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const from = `${dateFrom}T00:00:00Z`
      const to = `${nextDate(dateTo)}T00:00:00Z`
      const paidFrom = tab === 'history' && !historyPaidDateFilter ? null : dateFrom
      const paidTo = tab === 'history' && !historyPaidDateFilter ? null : nextDate(dateTo)
      const detectedFrom =
        tab === 'history' && detectedWithinDays > 0
          ? new Date(Date.now() - detectedWithinDays * 86_400_000).toISOString()
          : null
      const detectedTo = tab === 'history' && detectedWithinDays > 0 ? new Date().toISOString() : null
      const [summaryRows, runRows, accountRows, sourceRows, stableRows] = await Promise.all([
        fetchMissingPaymentsControlSummary({
          from,
          to,
          detectedFrom,
          detectedTo,
          paidFrom,
          paidTo,
        }),
        fetchMissingPaymentsControlRuns(10),
        // Справочник аккаунтов — отдельный лёгкий вызов (≈185 строк) и не должен
        // ронять экран: без него строки просто останутся без подписи по аккаунту.
        fetchRevenueAccountDirectory().catch(() => [] as RevenueAccountDirectoryRow[]),
        // Реестр статусов источника — мастер-данные соседнего экрана, его
        // миграция может быть ещё не применена. Поэтому это необязательное
        // обогащение: без него экран работает, просто не знает про RETIRED.
        fetchCrmSourceAccounts().catch(() => [] as CrmSourceAccountRow[]),
        // Stable OPEN records own the queue; payment journal rows remain only
        // evidence. During staged rollout this optional request may be absent.
        fetchRevenueSyncIncidents({ status: 'OPEN', limit: 500 }).catch(
          () => null as RevenueSyncIncidentRow[] | null
        ),
      ])
      setSummary(summaryRows)
      setAccounts(new Map(accountRows.map(row => [row.account, row])))
      setSourceAccounts(new Map(sourceRows.map(row => [row.account, row])))
      setOpenSyncIncidents(stableRows)
      setRuns(runRows)
    } catch (loadError: unknown) {
      setError(
        loadError instanceof Error ? loadError.message : 'Не удалось загрузить аудит синхронизации'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Выбор всегда должен указывать на строку текущей вкладки, иначе панель
  // деталей показывает инцидент, которого в списке нет.
  useEffect(() => {
    setSelectedKey(current => {
      if (current && tabIncidents.some(incident => incident.key === current)) return current
      return tabIncidents[0]?.key ?? null
    })
  }, [tabIncidents])

  useEffect(() => {
    if (!selected) {
      setItems([])
      return
    }

    let cancelled = false
    setItems([])
    setItemsHasMore(false)
    setItemsLoading(true)

    loadItemsPage(selected, false)
      .then(() => {
        // state обновляет сама функция; здесь только ждём завершения
      })
      .catch(itemError => {
        if (!cancelled) {
          setError(itemError instanceof Error ? itemError.message : 'Не удалось загрузить платежи')
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dateFrom, dateTo, detectedWithinDays, historyPaidDateFilter, selected, tab])

  async function loadItemsPage(incident: SourceIncident, append: boolean) {
    const paidFrom = tab === 'history' && !historyPaidDateFilter ? null : dateFrom
    const paidTo = tab === 'history' && !historyPaidDateFilter ? null : nextDate(dateTo)
    const detectedFrom =
      tab === 'history' && detectedWithinDays > 0
        ? new Date(Date.now() - detectedWithinDays * 86_400_000).toISOString()
        : null
    const detectedTo = tab === 'history' && detectedWithinDays > 0 ? new Date().toISOString() : null
    const cursor = append ? items[items.length - 1] : null

    try {
      const page = await fetchMissingPaymentsControlItemsPage({
        detectedFrom,
        detectedTo,
        paidFrom,
        paidTo,
        issueType: incident.issueType,
        account: incident.account,
        studioNumber: null,
        limit: ITEMS_PAGE_SIZE,
        afterDetectedAt: cursor?.detected_at ?? null,
        afterPaymentItemId: cursor?.payment_item_id ?? null,
      })
      setItems(current => (append ? [...current, ...page.rows] : page.rows))
      setItemsHasMore(page.hasMore)
    } catch (pageError) {
      if (append) throw pageError
      const from = `${dateFrom}T00:00:00Z`
      const to = `${nextDate(dateTo)}T00:00:00Z`
      const rows = await fetchMissingPaymentsControlItems({
        from,
        to,
        issueType: incident.issueType,
        account: incident.account,
        studioNumber: null,
        limit: 500,
      })
      setItems(rows)
      setItemsHasMore(false)
    }
  }

  async function loadMoreItems() {
    if (!selected || itemsLoadingMore) return
    setItemsLoadingMore(true)
    try {
      await loadItemsPage(selected, true)
    } catch (itemError: unknown) {
      setError(itemError instanceof Error ? itemError.message : 'Не удалось загрузить платежи')
    } finally {
      setItemsLoadingMore(false)
    }
  }

  function applyRolling35Preset() {
    if (!lastRolling35) return
    // Границы берём из самого прогона, а не считаем в UI: иначе экран и ETL
    // разойдутся в том, что считать «последним rolling35».
    setDateFrom(lastRolling35.window_start.slice(0, 10))
    setDateTo(lastRolling35.window_end.slice(0, 10))
  }

  function openAccount(account: string) {
    const params = new URLSearchParams({ account })
    if (deepLinkIncident) params.set('incident', deepLinkIncident)
    navigate(`/management/crm-accounts?${params.toString()}`)
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate('/management/hub')}
            className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Направления
          </button>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Аудит синхронизации платежей</h1>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Только чтение. Карантин, постоянное закрытие и возврат источника —{' '}
            <button
              type="button"
              onClick={() => navigate('/management/crm-accounts')}
              className="text-primary hover:underline"
            >
              на экране CRM-аккаунтов
            </button>
            .
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={event => setDateFrom(event.target.value)}
            title="Начало окна дат платежа"
            className="h-9 rounded border border-border bg-background px-2 text-sm outline-none focus:border-primary"
          />
          <input
            type="date"
            value={dateTo}
            onChange={event => setDateTo(event.target.value)}
            title="Конец окна дат платежа"
            className="h-9 rounded border border-border bg-background px-2 text-sm outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={applyRolling35Preset}
            disabled={!lastRolling35}
            title={
              lastRolling35
                ? `Окно последнего успешного rolling35: ${formatDate(lastRolling35.window_start)} - ${formatDate(lastRolling35.window_end)}`
                : 'Успешного прогона rolling35 в загруженной истории нет'
            }
            className="inline-flex h-9 items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Последний rolling35
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            Обновить
          </button>
        </div>
      </div>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5 p-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Требует действия
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard
            label="Открытые инциденты"
            value={stats.openCount}
            tone={stats.openCount > 0 ? 'text-red-700' : undefined}
          />
          <KpiCard
            label="Источники в карантине"
            value={stats.quarantined}
            tone={stats.quarantined > 0 ? 'text-sky-700' : undefined}
          />
          <KpiCard
            label="Не выбран филиал"
            value={stats.offBranch}
            tone={stats.offBranch > 0 ? 'text-red-700' : undefined}
          />
          <KpiCard label="Служебные аккаунты" value={stats.service} />
        </div>
        <div className="pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Обработано автоматически
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard label="Исключено из выручки" value={stats.deletedCount} />
          <KpiCard label="Сумма исключений" value={stats.deletedAmountText} />
        </div>
      </div>

      {quarantinedIncidents.length > 0 && (
        <Card className="border-sky-200 bg-sky-50/60 p-3">
          <div className="text-sm font-medium text-sky-900">
            Источники в карантине: {quarantinedIncidents.length}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {quarantinedIncidents.map(incident => {
              const silent = daysSince(incident.newestPaidOn)
              return (
                <button
                  key={incident.key}
                  type="button"
                  onClick={() => openAccount(incident.account)}
                  className="rounded border border-sky-200 bg-background px-2 py-1 text-xs hover:bg-sky-100"
                  title="Открыть карточку источника"
                >
                  {incidentTitle(incident)}
                  {silent != null && ` · молчит ${silent} дн.`}
                  {incident.reviewOverdue && ' · просрочен пересмотр'}
                </button>
              )
            })}
          </div>
          <div className="mt-2 text-xs text-sky-900/80">
            Платежи карантинных источников остаются живыми и в списки ниже не попадают. Снять
            карантин можно только на экране CRM-аккаунтов.
          </div>
        </Card>
      )}

      <Card className="p-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="inline-flex shrink-0 flex-wrap rounded-md border border-border bg-muted/30 p-0.5">
            {TABS.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setTab(item.value)}
                title={item.hint}
                className={cn(
                  'h-7 whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors',
                  tab === item.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          {tab === 'history' && (
            <>
              <select
                value={detectedWithinDays}
                onChange={event => setDetectedWithinDays(Number(event.target.value))}
                className="h-8 shrink-0 rounded border border-border bg-background px-2 text-xs outline-none focus:border-primary"
              >
                {DETECTED_WITHIN_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded border border-border bg-background px-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={historyPaidDateFilter}
                  onChange={event => setHistoryPaidDateFilter(event.target.checked)}
                  className="h-3.5 w-3.5"
                />
                Дата платежа
              </label>
            </>
          )}
          {tab !== 'runs' && (
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Поиск по студии, аккаунту, франчайзи или городу"
                className="w-full rounded border border-border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {TABS.find(item => item.value === tab)?.hint}
        </div>
      </Card>

      {tab === 'runs' ? (
        <RunsPanel runs={runs} />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(520px,0.9fr)]">
          <Card className="overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <div className="text-sm font-semibold">Инциденты источника</div>
              <Badge>{tabIncidents.length}</Badge>
            </div>
            {loading ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">Загрузка...</div>
            ) : tabIncidents.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                Наблюдений по фильтру нет
              </div>
            ) : (
              tabIncidents.map(incident => (
                <IncidentRow
                  key={incident.key}
                  incident={incident}
                  source={sourceAccounts.get(incident.account) ?? null}
                  selected={incident.key === selectedKey}
                  onSelect={() => setSelectedKey(incident.key)}
                />
              ))
            )}
          </Card>

          <IncidentDetails
            incident={selected}
            source={selectedSource}
            items={items}
            loading={itemsLoading}
            hasMore={itemsHasMore}
            loadingMore={itemsLoadingMore}
            onLoadMore={() => void loadMoreItems()}
            onOpenAccount={openAccount}
          />
        </div>
      )}
    </div>
  )
}
